const KEYBOARD_OPEN_TIMEOUT_MS = 2000;
// ignore small viewport changes (URL bar collapse, scrollbars) — only react to the on-screen keyboard
const KEYBOARD_MIN_HEIGHT_PX = 100;
const VISIBLE_MARGIN_PX = 8;

/**
 * Keeps an element (typically an input plus the error message rendered below it) visible when the
 * on-screen keyboard opens after a focus.
 *
 * Pass `scrollContainer` when the element sits in its own scrollport rather than the document —
 * inside a `position: fixed` modal, for instance, where scrolling the document achieves nothing.
 * Defaults to the window.
 */
export function keepVisibleAboveKeyboard(
	element: HTMLElement,
	{ bottomAllowancePx = 0, scrollContainer }: { bottomAllowancePx?: number; scrollContainer?: HTMLElement | null } = {},
): void {
	const viewport = window.visualViewport;
	if (!viewport) return;
	const initialHeight = viewport.height;

	const cleanup = () => {
		viewport.removeEventListener('resize', onResize);
		clearTimeout(timer);
	};

	const onResize = () => {
		if (initialHeight - viewport.height < KEYBOARD_MIN_HEIGHT_PX) return;
		const { top, bottom } = element.getBoundingClientRect();
		const visibleTop = viewport.offsetTop;
		const visibleBottom = viewport.offsetTop + viewport.height;
		const overflow = bottom + bottomAllowancePx + VISIBLE_MARGIN_PX - visibleBottom;
		// never push the element's top edge above the visible area while revealing its bottom edge
		const maxScroll = Math.max(top - visibleTop - VISIBLE_MARGIN_PX, 0);
		// The maths is scroller-agnostic: `getBoundingClientRect` and the visual viewport are both in
		// layout coordinates, and scrolling either the document or an inner scrollport by `overflow`
		// moves the element up by the same amount. `scrollBy` clamps to the available range.
		if (overflow > 0) (scrollContainer ?? window).scrollBy({ top: Math.min(overflow, maxScroll) });
		cleanup();
	};

	viewport.addEventListener('resize', onResize);
	const timer = setTimeout(cleanup, KEYBOARD_OPEN_TIMEOUT_MS);
}
